import { useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';

import { LoomChoiceVariantA } from '@/components/signup/loom-land-opt-in/loom-choice-variant-a';
import { LoomChoiceVariantB } from '@/components/signup/loom-land-opt-in/loom-choice-variant-b';
import {
	fireOptInPageRedirected,
	getOptInRedirectReason,
	isOptInSloFailure,
} from '@/components/signup/loom-land-opt-in/opt-in-analytics';
import { useLoomLandOptIn } from '@/components/signup/loom-land-opt-in/use-loom-land-opt-in';
import {
	resolveLoomEdition,
	useTwcPrice,
} from '@/components/signup/loom-land-opt-in/use-twc-price';
import { ProductKey } from '@/components/signup/utils/products';
import { ufoEventLoomLandOptInModalVisible } from '@/components/signup/utils/ufo-events';
import { UfoCustomTimingKey } from '@/components/signup/utils/ufo.types';
import { useLocation } from '@/utils/location-provider';

/**
 * TWCG-2108 Loom Land opt-in choice page.
 *
 * Route: /try/cloud/loom/choose
 *
 * Branches on the `twcg2108LoomLandOptIn` multivariant experiment cohort:
 * - control:  fall through to the existing Loom-only signup flow.
 * - radio:    {@link LoomChoiceVariantA} (multi-option "TWC choice" card).
 * - longText: {@link LoomChoiceVariantB} (single-option TWC pitch).
 *
 * This screen is read-only w.r.t. the experiment: it only consumes the resolved
 * cohort to render the correct arm. Exposure is owned by `/try/cloud/signup`,
 * where the treatment (the continue-URL rewrite) applies to the whole Loom-intent
 * population, so this page does NOT fire exposure.
 */

const pageStyles = xcss({
	minHeight: '100vh',
	width: '100%',
	backgroundColor: 'elevation.surface',
});

const centeredStyles = xcss({
	minHeight: '100vh',
	padding: 'space.400',
});

const PRICE_FETCH_TIMING = UfoCustomTimingKey.LOOM_LAND_OPT_IN_PRICE_FETCH;

const redirectToLoomOnlySignup = (search: string) => {
	// Control cohort keeps today's behaviour: the standard Loom signup page.
	window.location.replace(`/try/cloud/signup${search}`);
};

export const LoomChoicePageTemplate = () => {
	const { cohort, isResolved, isEnrolled, hasResolveTimedOut } = useLoomLandOptIn();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const location = useLocation();
	// Live, localized choice-card price for the Loom edition (from the `edition`
	// query param); `mode` picks the copy. Fetched client-side (no SSR pricing), so
	// we hold the spinner while loading rather than flash the default price.
	const edition = resolveLoomEdition(new URLSearchParams(location.search).get('edition'));
	const {
		price,
		mode: priceMode,
		isLoading: isPriceLoading,
		hasError: hasPriceError,
	} = useTwcPrice(edition);

	// UFO "choose page → modal visible" SLO (STARB-5214). UFO times the
	// experience from `start()` to whichever of success / failure / abort ends it;
	// the ref guarantees exactly one terminal call per page view.
	const hasFinishedUfoRef = useRef(false);

	useEffect(() => {
		void ufoEventLoomLandOptInModalVisible.start({ productKey: ProductKey.LOOM });
		// The live price fetch starts on mount too (see `useTwcPrice`).
		void ufoEventLoomLandOptInModalVisible.startSubstep(PRICE_FETCH_TIMING);
	}, []); // Once per page view.

	const isPriceSettled = !isPriceLoading || hasPriceError;
	useEffect(() => {
		if (isPriceSettled) {
			void ufoEventLoomLandOptInModalVisible.endSubstep(PRICE_FETCH_TIMING);
		}
	}, [isPriceSettled]);

	// Fall through to the Loom-only signup whenever we are not going to show a
	// treatment arm:
	//  - resolved control cohort, or
	//  - definitively not enrolled (indeterminate / not-enrolled — e.g. no Loom
	//    intent or signed out), or
	//  - the selected edition is Enterprise, which has no monthly per-user rate,
	//    so there's no meaningful "how much more" to pitch — skip the screen, or
	//  - the price fetch failed or timed out: without a price the cards have
	//    nothing to pitch, so bail to the standard signup instead of spinning, or
	//  - enrolled but the arm can't resolve: Statsig initialised without a value,
	//    or didn't initialise within COHORT_RESOLVE_TIMEOUT_MS (it reports
	//    enrolled-but-unresolved, not "not enrolled").
	// The brief "enrolled but arm not yet resolved" window shows a spinner and is
	// capped by that timeout, so users never get stuck on a blank page.
	const redirectReason = getOptInRedirectReason({
		isEnrolled,
		isResolved,
		cohort,
		edition,
		hasPriceError,
		hasCohortTimedOut: hasResolveTimedOut,
	});
	const shouldRedirect = redirectReason !== undefined;

	// Report why the modal was skipped, once per page view. Declared before the
	// redirect effect so the event is fired before navigation starts.
	const hasReportedRedirectRef = useRef(false);

	useEffect(() => {
		if (!redirectReason || hasReportedRedirectRef.current) {
			return;
		}
		hasReportedRedirectRef.current = true;
		// `cohort` defaults to "control" until resolved, so only report it then.
		const reportedCohort = isResolved ? cohort : undefined;
		fireOptInPageRedirected({
			createAnalyticsEvent,
			reason: redirectReason,
			cohort: reportedCohort,
			edition,
		});

		if (!hasFinishedUfoRef.current) {
			hasFinishedUfoRef.current = true;
			const ufoOptions = {
				customData: {
					loomLandOptInRedirectReason: redirectReason,
					loomLandOptInCohort: reportedCohort,
				},
			};
			// Eligible users who should have seen the modal count against the SLO;
			// intentional skips (control, not enrolled, Enterprise) are scrubbed.
			void (isOptInSloFailure(redirectReason)
				? ufoEventLoomLandOptInModalVisible.failure(ufoOptions)
				: ufoEventLoomLandOptInModalVisible.abort(ufoOptions));
		}
	}, [redirectReason, createAnalyticsEvent, isResolved, cohort, edition]);

	// The modal (either arm) is on screen: stop the SLO timer as a good event.
	// Parent effects run after the child's mount, so this fires once it's rendered.
	const isModalVisible = !shouldRedirect && isResolved && !isPriceLoading;
	useEffect(() => {
		if (!isModalVisible || hasFinishedUfoRef.current) {
			return;
		}
		hasFinishedUfoRef.current = true;
		void ufoEventLoomLandOptInModalVisible.success({
			customData: { loomLandOptInCohort: cohort },
		});
	}, [isModalVisible, cohort]);

	useEffect(() => {
		if (shouldRedirect) {
			redirectToLoomOnlySignup(location.search);
		}
	}, [shouldRedirect, location.search]);

	if (shouldRedirect || !isResolved) {
		// Redirecting (handled in the effect above), or still waiting for Statsig
		// to resolve the arm for an enrolled user. Show a spinner rather than a
		// blank page — `location.replace` and the resolve wait both take a moment.
		return (
			<Box xcss={pageStyles}>
				<Flex xcss={centeredStyles} alignItems="center" justifyContent="center">
					<Spinner label="Loading" testId="loom-choice-page-spinner" />
				</Flex>
			</Box>
		);
	}

	return (
		<Box xcss={pageStyles}>
			<Flex xcss={centeredStyles} alignItems="center" justifyContent="center">
				{/* Arm resolved. While the live price is still loading, show a spinner
				    in place of the cards so they don't flash the default price first. */}
				{isPriceLoading ? (
					<Spinner label="Loading" testId="loom-choice-price-spinner" />
				) : /* `'longText'` is the LoomChoiceVariantB cell of the
				      twcg-2108-loom-land-opt-in experiment; the three cell names
				      (control/radio/longText) match the Statsig console exactly —
				      see experiments.twcg2108LoomLandOptIn in the experiment manifest. */
				cohort === 'longText' ? (
					<LoomChoiceVariantB
						price={price}
						priceMode={priceMode}
						edition={edition}
						search={location.search}
						cohort={cohort}
					/>
				) : (
					<LoomChoiceVariantA
						price={price}
						priceMode={priceMode}
						edition={edition}
						search={location.search}
						cohort={cohort}
					/>
				)}
			</Flex>
		</Box>
	);
};
